import { UserProfile } from "@clerk/react"
import { shadcn } from "@clerk/ui/themes"
import { useAppSelector } from "hooks/storeHooks"
import { Building2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { selectUserInfo } from "store/user/userSlice"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

type ReadonlyRowProps = {
  label: string
  value: string
}

const ReadonlyRow = ({ label, value }: ReadonlyRowProps) => (
  <div className="flex flex-col gap-1 rounded-md border p-3">
    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span className="text-sm font-medium text-foreground">{value}</span>
  </div>
)

export const UserSettingsPage = () => {
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)

  const accountDisplayName = userInfo.displayName?.trim() || userInfo.username
  const merchantIdValue = userInfo.merchantId || t("userSettingsPage.notAvailable")
  const groupsValue = userInfo.groups.length
    ? userInfo.groups.join(", ")
    : t("userSettingsPage.noGroups")

  const detailsContent = (
    <>
      <ReadonlyRow label={t("userSettingsPage.accountLabel")} value={accountDisplayName} />
      <ReadonlyRow label={t("username")} value={userInfo.username} />
      <ReadonlyRow label={t("merchantId")} value={merchantIdValue} />
      <ReadonlyRow label={t("userSettingsPage.groupsLabel")} value={groupsValue} />
    </>
  )

  return (
    <div
      className="flex min-h-[calc(100vh-3rem)] items-center justify-center p-6"
      data-testid="user-settings-page"
    >
      <div className="w-full overflow-x-hidden">
        <div className="mx-auto w-fit max-w-full">
          <div className="origin-center [@media(min-width:1280px)]:scale-[0.92]">
            <UserProfile path="/user-settings" routing="path" appearance={{ theme: shadcn }}>
              <UserProfile.Page
                label={t("userSettingsPage.workspacePageLabel")}
                url="workspace"
                labelIcon={<Building2 />}
              >
                <Card className="max-w-2xl" data-testid="user-settings-workspace-card">
                  <CardHeader>
                    <CardTitle>{t("userSettingsPage.workspacePageLabel")}</CardTitle>
                    <CardDescription>
                      {t("userSettingsPage.workspacePageDescription")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-3">{detailsContent}</CardContent>
                </Card>
              </UserProfile.Page>
            </UserProfile>
          </div>
        </div>
      </div>
    </div>
  )
}
